/*
  Passkeys in the browser (01-login #9) — WebAuthn without an extra library.

  The server (backend/auth_passkeys.py, py_webauthn) sends standard JSON options (binary fields as base64url);
  the browser API wants ArrayBuffers, and the answer goes back as JSON again. These helpers do that conversion,
  call navigator.credentials.create() / .get() and say in plain words what went wrong.

  passkeySupport()          → { supported, platform: Promise<boolean> } (platform = Face ID / fingerprint built in)
  passkeyLabel()            → "Face ID" on iPhone / iPad / Mac, "fingerprint" on Android, else "a passkey"
  createPasskey(options)    → credential JSON for /auth/passkeys/register/verify
  getPasskey(options)       → credential JSON for /auth/passkeys/login/verify
  describePasskeyError(err) → { kind: 'cancelled' | 'unsupported' | 'other', message }
*/

const hasWindow = typeof window !== 'undefined';

export function base64urlToBuffer(value) {
  const base64 = String(value || '').replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = typeof atob === 'function' ? atob(padded) : Buffer.from(padded, 'base64').toString('binary');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function bufferToBase64url(buffer) {
  if (!buffer) return '';
  const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  const base64 = typeof btoa === 'function' ? btoa(binary) : Buffer.from(binary, 'binary').toString('base64');
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

const descriptors = (list) => (Array.isArray(list) ? list.map((item) => ({ ...item, id: base64urlToBuffer(item.id) })) : undefined);

/** Server JSON → PublicKeyCredentialCreationOptions. */
export function toCreationOptions(options) {
  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    user: { ...options.user, id: base64urlToBuffer(options.user?.id) },
    excludeCredentials: descriptors(options.excludeCredentials) || [],
  };
}

/** Server JSON → PublicKeyCredentialRequestOptions. */
export function toRequestOptions(options) {
  const result = { ...options, challenge: base64urlToBuffer(options.challenge) };
  const allow = descriptors(options.allowCredentials);
  if (allow && allow.length) result.allowCredentials = allow;
  else delete result.allowCredentials;
  return result;
}

/** A PublicKeyCredential from create() → the JSON py_webauthn reads. */
export function registrationToJSON(credential) {
  const response = credential.response || {};
  const transports = typeof response.getTransports === 'function' ? response.getTransports() : [];
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      attestationObject: bufferToBase64url(response.attestationObject),
      transports,
    },
    authenticatorAttachment: credential.authenticatorAttachment || undefined,
    clientExtensionResults: typeof credential.getClientExtensionResults === 'function' ? credential.getClientExtensionResults() : {},
  };
}

/** A PublicKeyCredential from get() → the JSON py_webauthn reads. */
export function authenticationToJSON(credential) {
  const response = credential.response || {};
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      authenticatorData: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : undefined,
    },
    authenticatorAttachment: credential.authenticatorAttachment || undefined,
    clientExtensionResults: typeof credential.getClientExtensionResults === 'function' ? credential.getClientExtensionResults() : {},
  };
}

export function isPasskeySupported() {
  return Boolean(hasWindow && window.PublicKeyCredential && navigator?.credentials?.create && navigator?.credentials?.get);
}

/** True when this device has Face ID / a fingerprint reader / Windows Hello built in. */
export async function hasPlatformAuthenticator() {
  if (!isPasskeySupported()) return false;
  try {
    const check = window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable;
    return typeof check === 'function' ? Boolean(await check.call(window.PublicKeyCredential)) : false;
  } catch {
    return false;
  }
}

/** What the person will use, in their words: "Face ID", "fingerprint" or "a passkey". */
export function passkeyLabel(userAgent = hasWindow ? navigator.userAgent || '' : '') {
  const agent = String(userAgent);
  if (/iPhone|iPad|iPod/i.test(agent)) return 'Face ID';
  if (/Android/i.test(agent)) return 'fingerprint';
  if (/Macintosh/i.test(agent) && hasWindow && navigator.maxTouchPoints > 1) return 'Face ID';
  return 'a passkey';
}

export async function createPasskey(options) {
  if (!isPasskeySupported()) throw Object.assign(new Error('unsupported'), { name: 'NotSupportedError' });
  const credential = await navigator.credentials.create({ publicKey: toCreationOptions(options) });
  if (!credential) throw Object.assign(new Error('cancelled'), { name: 'NotAllowedError' });
  return registrationToJSON(credential);
}

export async function getPasskey(options) {
  if (!isPasskeySupported()) throw Object.assign(new Error('unsupported'), { name: 'NotSupportedError' });
  const credential = await navigator.credentials.get({ publicKey: toRequestOptions(options) });
  if (!credential) throw Object.assign(new Error('cancelled'), { name: 'NotAllowedError' });
  return authenticationToJSON(credential);
}

/** Browser errors from create()/get() in plain words. Server errors (axios) are left to getErrorMessage. */
export function describePasskeyError(error) {
  const name = error?.name || '';
  if (name === 'NotAllowedError' || name === 'AbortError') {
    return { kind: 'cancelled', message: 'Passkey sign-in was cancelled or timed out.' };
  }
  if (name === 'NotSupportedError' || name === 'SecurityError') {
    return { kind: 'unsupported', message: 'This browser or address can’t use passkeys. Use your password instead.' };
  }
  if (name === 'InvalidStateError') {
    return { kind: 'exists', message: 'This device already has a passkey for your account.' };
  }
  return { kind: 'other', message: '' };
}

/* Per device: whether this person already set up (or declined) a passkey here, so the offer is shown once. */
const offerKey = (userId) => `sara.passkey.${userId || 'anon'}`;

export function readPasskeyChoice(userId) {
  try {
    return localStorage.getItem(offerKey(userId)) || '';
  } catch {
    return '';
  }
}

export function writePasskeyChoice(userId, value) {
  try {
    if (value) localStorage.setItem(offerKey(userId), value);
    else localStorage.removeItem(offerKey(userId));
  } catch {
    // storage blocked — the offer may show again, nothing worse
  }
}

/* Set right after a password sign-in in this tab: the shell may then offer "Use Face ID next time" once. */
const OFFER_FLAG = 'sara.passkey.offer';

export function markPasskeyOffer() {
  try {
    sessionStorage.setItem(OFFER_FLAG, '1');
  } catch {
    // no offer then
  }
}

export function hasPasskeyOffer() {
  try {
    return sessionStorage.getItem(OFFER_FLAG) === '1';
  } catch {
    return false;
  }
}

export function clearPasskeyOffer() {
  try {
    sessionStorage.removeItem(OFFER_FLAG);
  } catch {
    // nothing stored then either
  }
}
